import type { ChowStatus, StorePalette } from '../../components/chow'

/**
 * ChooseChow core catalog and plan content.
 * Supplies the presentation copy, default assets, and configuration for the 
 * ChooseChow marketplace, including chefs, dishes, categories, and subscription 
 * tiers. This serves as the primary data source until live projections or dynamic 
 * vendor records are fully bound.
 */

export interface Chef {
  name: string
  glyph: string
  /**
   * Seeded avatar served by the media route (/seed/...). Used
   * as the curated fallback image; live vendor records resolve their own avatar
   * via the chef directory. Absent = render the emoji glyph.
   */
  image?: string
  category: string
  cuisine: string
  area: string
  tags: readonly string[]
  rating: number
  subs: number
  priceFrom: number
  status: ChowStatus
  /** Server-derived verification badge tier (live rows only). */
  badge?: string
}

export const chefs: readonly Chef[] = [
  {
    name: 'Amara Okafor',
    glyph: '👩🏾‍🍳',
    image: '/seed/chef/amara.webp',
    category: 'Home Chef',
    cuisine: 'Nigerian & West African',
    area: 'Lekki, Lagos',
    tags: ['Jollof', 'Egusi', 'Suya'],
    rating: 4.9,
    subs: 89,
    priceFrom: 3500,
    status: 'open',
  },
  {
    name: 'Chef Tunde',
    glyph: '👨🏿‍🍳',
    image: '/seed/chef/tunde.webp',
    category: 'Home Chef',
    cuisine: 'Continental & Fusion',
    area: 'Maitama, Abuja',
    tags: ['Pasta', 'Grills', 'Healthy'],
    rating: 4.7,
    subs: 64,
    priceFrom: 4200,
    status: 'open',
  },
  {
    name: 'Naija Street Bites',
    glyph: '🚚',
    image: '/seed/chef/street_bites.webp',
    category: 'Food Truck',
    cuisine: 'Street Food',
    area: 'Wuse, Abuja',
    tags: ['Shawarma', 'Burgers'],
    rating: 4.8,
    subs: 201,
    priceFrom: 1800,
    status: 'open',
  },
  {
    name: "Laila's Patisserie",
    glyph: '🧁',
    image: '/seed/chef/laila.webp',
    category: 'Bakery',
    cuisine: 'Pastry & Baked Goods',
    area: 'Garki, Abuja',
    tags: ['Cakes', 'Bread', 'Pastry'],
    rating: 5,
    subs: 43,
    priceFrom: 2000,
    status: 'open',
  },
  {
    name: 'Mama T Catering',
    glyph: '🍲',
    image: '/seed/chef/mama_t.webp',
    category: 'Caterer',
    cuisine: 'Events & Large Orders',
    area: 'Asokoro, Abuja',
    tags: ['Jollof', 'Small Chops'],
    rating: 4.6,
    subs: 128,
    priceFrom: 15000,
    status: 'closed',
  },
]

export type DishCategory = string

export interface Dish {
  rating?: number
  id?: string
  name: string
  chef: string
  chefGlyph: string
  glyph: string
  /**
   * Seeded dish photo served by the media route. Rendered when reachable (full
   * stack), falling back to the emoji glyph on the bare frontend dev server.
   */
  image?: string
  price: number
  category: DishCategory
  stock?: number
  status?: 'available' | 'sold_out' | 'hidden'
}

export const dishes: readonly Dish[] = [
  { name: 'Smoky Jollof Rice', chef: 'Amara Okafor', chefGlyph: '👩🏾‍🍳', glyph: '🍛', image: '/seed/dish/jollof.webp', price: 4500, category: 'rice', stock: 8, status: 'available' },
  { name: 'Egusi Soup & Pounded Yam', chef: 'Amara Okafor', chefGlyph: '👩🏾‍🍳', glyph: '🥘', image: '/seed/dish/egusi.webp', price: 5000, category: 'soups', stock: 12, status: 'available' },
  { name: 'Suya Skewers', chef: 'Amara Okafor', chefGlyph: '👩🏾‍🍳', glyph: '🍢', image: '/seed/dish/suya.webp', price: 3500, category: 'grill', stock: 0, status: 'sold_out' },
  { name: 'Akara Breakfast', chef: 'Amara Okafor', chefGlyph: '👩🏾‍🍳', glyph: '🧆', image: '/seed/dish/akara.webp', price: 2800, category: 'breakfast', stock: 4, status: 'available' },
  { name: 'Ofada Rice & Stew', chef: 'Chef Tunde', chefGlyph: '👨🏿‍🍳', glyph: '🍚', image: '/seed/dish/ofada.webp', price: 4200, category: 'rice', stock: 15, status: 'available' },
  { name: 'Grilled Tilapia', chef: 'Chef Tunde', chefGlyph: '👨🏿‍🍳', glyph: '🐟', image: '/seed/dish/tilapia.webp', price: 6500, category: 'protein', stock: 0, status: 'sold_out' },
  
  // Additional chef dishes
  { name: 'Special Egusi & Pounded Yam', chef: 'Amara Okafor', chefGlyph: '👩🏾‍🍳', glyph: '🥘', image: '/seed/dish/egusi.webp', price: 5200, category: 'soups', stock: 12, status: 'available' },
  { name: 'Abuja Beef Suya Skewers', chef: 'Chef Tunde', chefGlyph: '👨🏿‍🍳', glyph: '🍢', image: '/seed/dish/suya.webp', price: 3800, category: 'grill', stock: 8, status: 'available' },
  { name: 'Flaky Meat Pie Box (Pack of 3)', chef: "Laila's Patisserie", chefGlyph: '🧁', glyph: '🥧', image: '/seed/dish/pastry.webp', price: 3500, category: 'breakfast', stock: 6, status: 'available' },
  { name: 'Celebration Velvet Cake Slice', chef: "Laila's Patisserie", chefGlyph: '🧁', glyph: '🍰', image: '/seed/dish/cake.webp', price: 3000, category: 'breakfast', stock: 10, status: 'available' },
  { name: 'Peppered Snapper & Yam Chips', chef: 'Chef Tunde', chefGlyph: '👨🏿‍🍳', glyph: '🐟', image: '/seed/dish/tilapia.webp', price: 6000, category: 'protein', stock: 5, status: 'available' },
  { name: 'Crispy Akara & Ogi Pap', chef: "Laila's Patisserie", chefGlyph: '🧁', glyph: '🧆', image: '/seed/dish/akara.webp', price: 2500, category: 'breakfast', stock: 14, status: 'available' }
]

export interface Store {
  category: string
  name: string
  glyph: string
  area: string
  status: ChowStatus
  hours: string
  rating: number
  etaMins: string
  items: number
  minOrder: number
  palette: StorePalette
  type: 'market' | 'grocery' | 'supermarket' | 'pharmacy'
}

export const stores: readonly Store[] = [
  {
    category: 'Market',
    name: '🏪 Wuse Central Market',
    glyph: '🏪',
    area: 'Wuse II, Abuja',
    status: 'open',
    hours: 'Mon–Sat 7AM–6PM',
    rating: 4.5,
    etaMins: '30–50 min',
    items: 12,
    minOrder: 2000,
    palette: 'red',
    type: 'market',
  },
  {
    category: 'Market',
    name: '🛒 Garki Modern Market',
    glyph: '🛒',
    area: 'Garki, Abuja',
    status: 'open',
    hours: 'Daily 8AM–7PM',
    rating: 4.3,
    etaMins: '25–40 min',
    items: 8,
    minOrder: 1500,
    palette: 'red',
    type: 'market',
  },
  {
    category: 'Grocery',
    name: '🏢 Jabi FoodCo',
    glyph: '🏢',
    area: 'Jabi, Abuja',
    status: 'open',
    hours: 'Daily 9AM–9PM',
    rating: 4.7,
    etaMins: '20–35 min',
    items: 6,
    minOrder: 3000,
    palette: 'green',
    type: 'grocery',
  },
  {
    category: 'Supermarket',
    name: '🏬 Ceddi Plaza Mart',
    glyph: '🏬',
    area: 'Central Area, Abuja',
    status: 'closed',
    hours: 'Opens 8AM',
    rating: 4.4,
    etaMins: '35–55 min',
    items: 21,
    minOrder: 5000,
    palette: 'orange',
    type: 'supermarket',
  },
]

export interface Txn {
  glyph: string
  title: string
  sub: string
  amount: number
  direction: 'in' | 'out'
}

export const transactions: readonly Txn[] = [
  { glyph: '💸', title: 'Wallet top-up', sub: 'Today · Paystack', amount: 20000, direction: 'in' },
  { glyph: '🍽️', title: 'Jollof Rice x1', sub: 'Today · Wallet', amount: 4500, direction: 'out' },
]

export const wallet = {
  balance: 12500,
  spent: 4500,
  added: 20000,
}

/* ── Subscriptions ───────────────────────────────────────────────────── */

export type TierCode = 'basic' | 'plus' | 'premium'

export interface SubscriptionTier {
  code: TierCode
  name: string
  priceMonthly: number
  target: string
  benefits: readonly string[]
  tag?: string
  scheduled: boolean
  /** Hosted Paystack payment page for this plan. */
  paystackUrl: string
}

/** App personal subscription tiers (ChooseChow Subscription Tech Spec v1.0). */
export const tiers: readonly SubscriptionTier[] = [
  {
    code: 'basic',
    name: 'ChooseChow Basic',
    priceMonthly: 2000,
    target: 'Light ordering and solo meal planning',
    benefits: ['Meal planning for the month', 'Standard support'],
    scheduled: true,
    paystackUrl: 'https://paystack.shop/pay/3v2xr1ol26',
  },
  {
    code: 'plus',
    name: 'ChooseChow Plus',
    priceMonthly: 5500,
    target: 'Weekly planners and busy homes',
    benefits: [
      'Meal planning for the month',
      'Priority orders',
      'Priority support',
    ],
    tag: 'Popular',
    scheduled: true,
    paystackUrl: 'https://paystack.shop/pay/ua5brlc6hr',
  },
  {
    code: 'premium',
    name: 'ChooseChow Premium',
    priceMonthly: 10000,
    target: 'Families & power users',
    benefits: [
      'Meal planning for the whole house',
      'Early chef access',
      'VIP support',
    ],
    tag: 'Best value',
    scheduled: true,
    paystackUrl: 'https://paystack.shop/pay/efvbqk0tnl',
  },
]

import { ReactNode } from 'react'
import { PackageIcon, BowlIcon, TruckIcon } from '../../components/icons'

export type PlanCadence = 'weekly' | 'monthly'

export interface ChefPlan {
  id: string
  chef: string
  glyph: ReactNode
  name: string
  cadence: PlanCadence
  price: number
  rating: number
  subscribers: number
  summary: string
  hot?: boolean
}

/** Chef-offered recurring meal plans (pay a chef for a weekly/monthly menu). */
export const chefPlans: readonly ChefPlan[] = [
  {
    id: 'plan-amara-weekly',
    chef: 'Amara Okafor',
    glyph: <PackageIcon size={48} />,
    name: 'Weekly Jollof Box',
    cadence: 'weekly',
    price: 18000,
    rating: 4.9,
    subscribers: 312,
    summary: '5 lunches — jollof, egusi, suya & sides',
    hot: true,
  },
  {
    id: 'plan-tunde-reset',
    chef: 'Chef Tunde',
    glyph: <BowlIcon size={48} />,
    name: 'Healthy Reset',
    cadence: 'weekly',
    price: 22000,
    rating: 4.7,
    subscribers: 140,
    summary: 'Grilled proteins & continental, 5 days',
  },
  {
    id: 'plan-laila-sweet',
    chef: "Laila's Patisserie",
    glyph: <PackageIcon size={48} />,
    name: 'Sweet Monthly',
    cadence: 'monthly',
    price: 30000,
    rating: 5,
    subscribers: 64,
    summary: 'A cake + pastry box, every fortnight',
  },
  {
    id: 'plan-naija-street',
    chef: 'Naija Street Bites',
    glyph: <TruckIcon size={48} />,
    name: 'Street Monthly',
    cadence: 'monthly',
    price: 15000,
    rating: 4.8,
    subscribers: 201,
    summary: 'Shawarma & burgers, weekend drops',
  },
]

/** The hottest chef plan, surfaced as the subscription "sizzle". */
export const spotlightPlan = chefPlans[0]

export const homeSpecial = {
  title: "Amara's Smoky Jollof & Grilled Chicken",
  subtitle: 'Fresh meals · Kitchen pickup',
  price: 4500,
}

export const marketSummary = {
  storeCount: 8,
  title: 'Groceries & Pharmacy',
  subtitle: 'Shop, reorder past purchases, track inventory.',
}

export interface CuratedMenu {
  id: string
  title: string
  chef: string
  chef_profile_id: string
  status: 'published'
  cover_image_url: string
  summary: string
  [key: string]: unknown
}

/** Curated menus for demo and offline viewports. */
export const curatedMenus: readonly CuratedMenu[] = [
  {
    id: 'menu-amara',
    title: 'Weekly Lagos Delicacies',
    chef: 'Amara Okafor',
    chef_profile_id: 'merchant-amara-okafor',
    status: 'published',
    cover_image_url: '/seed/menu/amara-weekly.webp',
    summary: 'Smoky Jollof, Egusi with Pounded Yam, Efo Riro & Suya',
  },
  {
    id: 'menu-tunde',
    title: 'Abuja Suya & Grill Feast',
    chef: 'Chef Tunde',
    chef_profile_id: 'merchant-chef-tunde',
    status: 'published',
    cover_image_url: '/seed/menu/tunde-reset.webp',
    summary: 'Night-market beef suya, grilled tilapia & peppered goat',
  },
  {
    id: 'menu-laila',
    title: 'Breakfast & Pastry Delights',
    chef: "Laila's Patisserie",
    chef_profile_id: 'merchant-laila-s-patisserie',
    status: 'published',
    cover_image_url: '/seed/menu/laila-sweet.webp',
    summary: 'Akara, Ogi, Meat Pies & Celebration Cakes',
  },
]
